import { useState } from 'react';
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-hot-toast";
import { Smile } from "lucide-react";
import api from "../../api/axios";
import { useLanguage } from "../../context/LanguageContext";

export default function MoodTracker({ onMoodSelect }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [mood, setMood] = useState('');

  const handleMoodSelect = async (selectedMood) => {
    if (submitting) return;

    // 🧘 GUEST LOGIC: Skip API, just update local state
    if (user?.isGuest) {
      setMood(selectedMood);
      onMoodSelect?.(selectedMood);
      return;
    }

    setSubmitting(true);
    try {
      if (user) {
        await api.post('/moods', {
          mood: selectedMood,
          score: getScore(selectedMood)
        });
      } else {
        // 🧘 Guest Support: Save to local storage for the graph to pick up
        const guestMoods = JSON.parse(localStorage.getItem('ayi_guest_moods') || '[]');
        guestMoods.push({
          id: 'guest_' + Date.now(),
          mood: selectedMood,
          score: getScore(selectedMood),
          createdAt: new Date().toISOString()
        });
        localStorage.setItem('ayi_guest_moods', JSON.stringify(guestMoods.slice(-7))); // Keep last 7
      }

      setMood(selectedMood);
      toast.success(t('dash_mood_recorded_toast') || "Mood recorded! thank you for sharing.");
      onMoodSelect?.(selectedMood);

    } catch (err) {
      console.error(err);
      toast.error(t('dash_mood_save_error') || "Could not save mood");
    } finally {
      setSubmitting(false);
    }
  };

  const getScore = (m) => {
    const scores = { "Happy": 5, "Neutral": 3, "Sad": 1, "Anxious": 2, "Hopeful": 5, "Overwhelmed": 1 };
    return scores[m] || 3;
  };

  return (
    <div className="bg-white/80 dark:bg-gray-900/50 backdrop-blur-xl p-6 rounded-2xl shadow-sm border border-indigo-50 dark:border-gray-800 transition-all duration-300 hover:shadow-md">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-display font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            {t('dash_mood_tracker')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('dash_mood_sub')}</p>
        </div>
        <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
          <Smile className="w-5 h-5 text-indigo-500" />
        </div>
      </div>

      <div className="flex justify-between items-center gap-4 py-2">
        {[
          { id: 'Sad', icon: '🌧️', color: 'blue', labelKey: 'dash_mood_heavy' },
          { id: 'Neutral', icon: '🍃', color: 'teal', labelKey: 'dash_mood_still' },
          { id: 'Happy', icon: '☀️', color: 'orange', labelKey: 'dash_mood_bright' }
        ].map((m) => (
          <button
            key={m.id}
            disabled={submitting}
            onClick={() => handleMoodSelect(m.id)}
            className={`
              flex-1 flex flex-col items-center gap-2 p-3 rounded-2xl transition-all duration-500 group relative
              ${mood === m.id
                ? `bg-${m.color}-50/50 dark:bg-${m.color}-900/20 ring-2 ring-${m.color}-200 dark:ring-${m.color}-800`
                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:scale-105 active:scale-95'
              }
            `}
          >
            <span className={`text-4xl transition-transform duration-500 group-hover:scale-125 ${mood === m.id ? 'scale-110 drop-shadow-md' : 'grayscale-[0.5]'}`}>
              {m.icon}
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-widest ${mood === m.id ? `text-${m.color}-600 dark:text-${m.color}-400` : 'text-gray-400'}`}>
              {t(m.labelKey)}
            </span>
            {mood === m.id && (
              <motion.div
                layoutId="activeMoodDot"
                className={`absolute -bottom-1 w-1.5 h-1.5 rounded-full bg-${m.color}-500 shadow-[0_0_8px_rgba(var(--${m.color}-rgb),0.6)]`}
              />
            )}
          </button>
        ))}
      </div>

      {mood && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-3 bg-gray-50/50 dark:bg-gray-800/30 rounded-xl text-center border border-dashed border-gray-200 dark:border-gray-700"
        >
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {t('dash_mood_feeling_msg_start')} <span className="font-bold text-gray-800 dark:text-gray-200">"{t('mood_' + mood.toLowerCase()) || mood}"</span>. {t('dash_mood_feeling_msg_end')}
          </p>
        </motion.div>
      )}
    </div>
  );
}